// Palco da mesa, nas coordenadas do protótipo: 390x844 em pé e 980x520 deitado, escalado para a janela. O palco nunca
// fica numa caixa com faixas vazias: a largura lógica (--W) estica até a borda da janela, e a mesa e a mão ficam
// centralizadas (--dx). Em pé, a partir de 600 px de largura (tablet), a largura vai até 640; deitado, até 1400.
// No computador (paisagem com 1024 px ou mais de largura e 600 px ou mais de altura), a tela toda, sem palco: quem
// desenha é a própria tela (a mesa monta a grade de 3 colunas da tela 5.5 do canvas).
// --compensa: quanto o texto e os botões da mesa crescem para continuar com 13 px e 48 px de verdade na tela quando o
// palco encolhe (celular deitado: o palco de 520 de altura cabe em 390).
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';

export type Modo = 'pe' | 'deitado' | 'computador';
export const COMPUTADOR = { largura: 1024, altura: 600 } as const;
const BASE = { pe: [390, 844], deitado: [980, 520] } as const;
const LARGURA_MAXIMA = { pe: 640, deitado: 1400 } as const;

export interface MedidaDoPalco { paisagem: boolean; escala: number; modo: Modo; largura: number }

export function medirPalco(w: number, h: number): MedidaDoPalco {
  const paisagem = w > h * 1.05;
  const modo: Modo = paisagem && w >= COMPUTADOR.largura && h >= COMPUTADOR.altura ? 'computador' : paisagem ? 'deitado' : 'pe';
  const tipo = paisagem ? 'deitado' : 'pe';
  const [bw, bh] = BASE[tipo];
  const escala = Math.min(w / bw, h / bh, paisagem ? 1.4 : 1.5);
  const largura = Math.round(Math.min(Math.max(bw, w / escala), LARGURA_MAXIMA[tipo]));
  return { paisagem, escala, modo, largura };
}

const medir = () => medirPalco(window.innerWidth, window.innerHeight);

export function usePalco(): MedidaDoPalco {
  const [m, setM] = useState(medir);
  useEffect(() => {
    const f = () => setM(medir());
    window.addEventListener('resize', f);
    window.addEventListener('orientationchange', f);
    return () => { window.removeEventListener('resize', f); window.removeEventListener('orientationchange', f); };
  }, []);
  return m;
}

// As variáveis de CSS do palco: largura lógica, deslocamento da mesa e compensação do texto.
export function variaveisDoPalco({ paisagem, escala, largura }: MedidaDoPalco): CSSProperties {
  const base = paisagem ? BASE.deitado[0] : BASE.pe[0];
  return {
    '--W': `${largura}px`,
    '--dx': `${(largura - base) / 2}px`,
    '--compensa': Math.min(Math.max(1, 1 / escala), 1.4).toFixed(3),
  } as CSSProperties;
}

// computador: só quem sabe desenhar a tela toda pede (a mesa); o resto segue no palco deitado.
export function Palco({ children, computador = false }: { children: (paisagem: boolean, modo: Modo) => ReactNode; computador?: boolean }) {
  const medida = usePalco();
  const { paisagem, escala, modo } = medida;
  if (computador && modo === 'computador') {
    return <div className="vista computador" aria-live="polite">{children(true, modo)}</div>;
  }
  const m: Modo = modo === 'computador' ? 'deitado' : modo;
  return (
    <div className="vista">
      <div className={'palco' + (paisagem ? ' land' : '')} style={{ ...variaveisDoPalco(medida), transform: `scale(${escala})` }} aria-live="polite">
        {children(paisagem, m)}
      </div>
    </div>
  );
}
